import { ReactNode, useEffect, useState } from 'react';
import { loadAnalytics } from 'modules/analytics';
import { FeatureFlag, useFeatureFlags } from 'modules/unleash';
import { env } from '../../../constants';
import { handleConsentEvent, initializeConsentManager } from '../utils';

export const ConsentManager = ({ children }: { children: ReactNode }) => {
  const { features, isLoading: isUnleashLoading } = useFeatureFlags();

  const [isCMPScriptLoaded, setIsCMPScriptLoaded] = useState(false);

  const isUserConsentEnabled =
    features?.[FeatureFlag.IS_USERCENTRICS_UI_ENABLED].enabled;

  useEffect(() => {
    if (!features || isUnleashLoading) {
      return;
    }

    const setupConsentManager = async () => {
      try {
        const isInitialized = await window.__ucCmp?.isInitialized();
        if (isInitialized) {
          initializeConsentManager();
        } else {
          window.addEventListener(
            'UC_UI_INITIALIZED',
            initializeConsentManager
          );
        }
      } catch (error) {
        console.error('Error initializing CMP:', error);
      }
    };

    if (!isUnleashLoading && isUserConsentEnabled && !isCMPScriptLoaded) {
      const script = document.createElement('script');
      script.id = 'usercentrics-cmp';
      script.dataset.settingsId = env.VITE_USERCENTRIC_KEY;
      script.src = 'https://web.cmp.usercentrics.eu/ui/loader.js';
      script.async = true;
      script.onload = () => {
        setIsCMPScriptLoaded(true);
        void setupConsentManager();
      };
      script.onerror = () => {
        console.error('Failed to load the Usercentrics script.');
      };
      document.body.appendChild(script);
    } else if (!isUnleashLoading && !isUserConsentEnabled) {
      void loadAnalytics();
    }

    return () => {
      window.removeEventListener('UC_UI_INITIALIZED', initializeConsentManager);
      window.removeEventListener('UC_CONSENT', () => handleConsentEvent(true));
    };
  }, [features, isUserConsentEnabled, isUnleashLoading, isCMPScriptLoaded]);

  return <>{children}</>;
};
